import { gql, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import DashboardAccountsWidget from '../../components/dashboard/DashboardAccountsWidget';
import DashboardMonthlySpendComparisonChart from '../../components/dashboard/DashboardMonthlySpendComparisonChart';
import DashboardWelcomeCard from '../../components/dashboard/DashboardWelcomeCard';
import { usePresetDateRange } from '../../components/DateRangePicker';
import Entity from '../../components/entity';
import PlaidLinkButton from '../../components/plaid/PlaidLinkButton';
import TransactionsTable, { TxnTableElementType } from '../../components/tables/TransactionsTable';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { PATHS } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';
import { calculateTotalTransactions } from '../../providers/TransactionsProvider';

import type { BankConnectionCheckQuery, BankConnectionCheckQueryVariables } from './__generated__/DashboardPane.gql';

const GET_BANK_CONNECTIONS = gql`
  query BankConnectionCheck {
    getBankConnectionsForUser {
      id
    }
  }
`;

const DASHBOARD_RECENT_LIMIT = 10;

const DashboardPane = () => {
  const { user, loading: authLoading } = useAuthState();
  const { transactions, loading: transactionsLoading } = useFilteredTransactions();
  const navigate = useNavigate();
  const thisYearRange = usePresetDateRange('thisYear');

  const {
    data: bankConnectionsData,
    refetch: refetchBankConnections,
    loading: bankConnectionsLoading,
  } = useQuery<BankConnectionCheckQuery, BankConnectionCheckQueryVariables>(GET_BANK_CONNECTIONS);

  const txnCountThisYear = React.useMemo(
    () => calculateTotalTransactions(transactions, thisYearRange),
    [transactions, thisYearRange]
  );

  if (authLoading || bankConnectionsLoading) {
    return <LoadingIndicator fullScreen />;
  }

  const plaidConnected = bankConnectionsData?.getBankConnectionsForUser?.length;

  if (!plaidConnected) {
    return (
      <div className="text-primary flex min-h-screen w-full flex-col items-center justify-center gap-4">
        <PlaidLinkButton
          onSuccess={() => {
            refetchBankConnections().catch((err) => {
              logger.error('Failed to refetch bank connections:', err);
            });
          }}
        >
          Connect your accounts
        </PlaidLinkButton>
      </div>
    );
  }

  const greetingName = user?.preferredName || user?.firstName;

  const welcomeCard = <DashboardWelcomeCard greetingName={greetingName} />;
  const chart = (
    <DashboardMonthlySpendComparisonChart key="chart" transactions={transactions} isLoading={transactionsLoading} />
  );
  const table = (
    <TransactionsTable
      key="table"
      primaryElement={TxnTableElementType.Merchant}
      limit={DASHBOARD_RECENT_LIMIT}
      title="Recent Transactions"
      subtitle={transactionsLoading ? undefined : `${txnCountThisYear} transactions this year`}
      hideFilters
      footerAction={
        <Button size="sm" color="secondary" onClick={() => navigate(PATHS.SPENDING())}>
          See All Transactions
        </Button>
      }
    />
  );
  const accountsWidget = <DashboardAccountsWidget key="accounts" />;

  return (
    <Entity.Pane>
      <Entity.Body
        className="mt-0 md:mt-3"
        primaryElement={welcomeCard}
        leftColumn={[chart, table]}
        rightColumn={accountsWidget}
        singleColumnOrderOverride={[welcomeCard, accountsWidget, chart, table]}
      />
    </Entity.Pane>
  );
};

export default DashboardPane;
